/**
 * LongPressRecordButton 组件
 * 支持长按录音和手势操作的高级录音按钮，类似微信录音
 * 使用 useVoiceVisualizerWrapper 解决快速点击问题
 * 桌面端支持点击保持模式，移动端保持长按模式
 */
import React, { memo, useCallback, useRef, useState, useEffect } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import { useVoiceVisualizerWrapper } from '../../../../hooks/v3/audio/useVoiceVisualizerWrapper';
import { useFrontendLogger } from '../../../../atoms/v3/debugAtoms';
import { addToastAtom } from '../../../../atoms/v0/uiAtoms';
import { sttProcessingAtom, isVoiceRecordingAtom } from '../../../../atoms/v3/chatInputAtoms';
import { messageSendingAtom } from '../../../../atoms/v3/chatAtoms';
import { isTouchDevice, supportsTactileFeedback } from '../../../../utils/platformUtils';
import { RecordingOverlay } from './RecordingOverlay';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';

interface LongPressRecordButtonProps {
  onRecordingComplete?: (blob: Blob, duration: number) => void;
  onRecordingCancel?: () => void;
  onRecordingEdit?: (blob: Blob, duration: number) => void;
  onTranscriptionComplete?: (text: string) => void; // 🔥 新增：STT转录完成回调
  enableSTT?: boolean; // 🔥 新增：是否启用STT功能
  isDisabled?: boolean;
  isEmbedded?: boolean; // 🔥 新增：是否为嵌入模式，影响覆盖层渲染方式
  className?: string;
  children?: React.ReactNode;
  maxRecordingDuration?: number; // 🔥 新增：最大录音时长（秒），默认15秒
  warningThreshold?: number; // 🔥 新增：提醒阈值（秒），默认提前10秒提醒
}

const LongPressRecordButtonComponent: React.FC<LongPressRecordButtonProps> = ({
  onRecordingComplete,
  onRecordingCancel,
  onRecordingEdit,
  onTranscriptionComplete,
  enableSTT = false,
  isDisabled = false,
  isEmbedded = false, // 🔥 新增：是否为嵌入模式
  className = "",
  children,
  maxRecordingDuration = 15, // 🔥 默认15秒最大录音时长
  warningThreshold = 10 // 🔥 默认提前10秒提醒
}) => {
  const logger = useFrontendLogger();
  const addToast = useSetAtom(addToastAtom);

  // 🔥 获取相关loading状态
  const isSTTProcessing = useAtomValue(sttProcessingAtom);
  const isMessageSending = useAtomValue(messageSendingAtom);
  const setIsVoiceRecording = useSetAtom(isVoiceRecordingAtom);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const activePointerIdRef = useRef<number | null>(null);
  const recordingStartTimeRef = useRef<number | null>(null);
  const lastRecordingDurationRef = useRef<number>(0);
  const lastProcessedBlobRef = useRef<Blob | null>(null); // 防止重复处理
  const supportsTactile = supportsTactileFeedback();

  // 手势状态
  const [gestureState, setGestureState] = useState<'normal' | 'dragging-cancel' | 'dragging-edit' | 'dragging-hold'>('normal');
  const [isDragging, setIsDragging] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState<number>(0);
  const [isDesktopHoldMode, setIsDesktopHoldMode] = useState(false); // 🔥 桌面端保持模式
  const [isMobileHoldMode, setIsMobileHoldMode] = useState(false); // 🔥 移动端保持模式
  const [hasShownWarning, setHasShownWarning] = useState(false); // 🔥 是否已显示时长警告

  // 使用 ref 来保存最新的回调函数，避免 useEffect 依赖项问题
  const callbacksRef = useRef({
    onRecordingComplete,
    onRecordingEdit,
    gestureState: 'normal' as 'normal' | 'dragging-cancel' | 'dragging-edit',
    isQuickRelease: false // 🔥 新增：标记是否为快速抬手
  });

  // 更新 ref 中的回调函数，但不自动更新 gestureState（手动控制）
  callbacksRef.current.onRecordingComplete = onRecordingComplete;
  callbacksRef.current.onRecordingEdit = onRecordingEdit;



  // 录音回调函数
  const handleRecordingStart = useCallback(() => {
    // 🔥 开始录音时重新启用STT
    enableSTTRef.current = enableSTT;
    // 🔥 重置快速抬手标志，确保新录音不受之前状态影响
    callbacksRef.current.isQuickRelease = false;
    setRecordingDuration(0);
    recordingStartTimeRef.current = Date.now();
    lastRecordingDurationRef.current = 0;
    setHasShownWarning(false); // 🔥 重置警告状态
    // 🔥 设置录音状态为true
    setIsVoiceRecording(true);
    logger.info('LongPressRecordButton: 📢 Recording started', {
      maxRecordingDuration,
      warningThreshold
    });
  }, [logger, enableSTT, maxRecordingDuration, warningThreshold, setIsVoiceRecording]);

  const handleRecordingStop = useCallback(() => {
    // 🔥 设置录音状态为false
    setIsVoiceRecording(false);
    logger.info('LongPressRecordButton: ⏹️ Recording stopped');
  }, [logger, setIsVoiceRecording]);

  const handleRecordingError = useCallback((error: Error) => {
    logger.error('LongPressRecordButton: ❌ Recording error', error);
  }, [logger]);

  // 🔥 使用ref来控制STT启用状态，避免异步状态更新问题
  const enableSTTRef = React.useRef(enableSTT);

  // 使用最终解决方案 - useVoiceVisualizerWrapper
  const recorderControls = useVoiceVisualizerWrapper({
    onStartRecording: handleRecordingStart,
    onStopRecording: handleRecordingStop,
    onErrorPlayingAudio: handleRecordingError,
    onTranscriptionComplete: onTranscriptionComplete, // 🔥 新增：STT转录完成回调
    enableSTT: enableSTT, // 🔥 保持原始设置
    enableSTTRef: enableSTTRef, // 🔥 新增：动态控制STT的ref
    onQuickRelease: () => {
      logger.info('LongPressRecordButton: 🚀 Quick release detected!');

      // 🔥 设置快速抬手标志，阻止后续的STT和发送流程
      callbacksRef.current.isQuickRelease = true;

      // 🔥 禁用STT，防止useVoiceVisualizerWrapper中的STT处理
      logger.info('LongPressRecordButton: 禁用STT以阻止快速录音的转录处理');
      enableSTTRef.current = false;

      // 🔥 显示 toast 提示
      addToast({
        title: '按下时间过短',
        description: '请长按录音按钮进行录音',
        status: 'warning',
        duration: 2000,
      });

      // 🔥 默认取消录音
      if (onRecordingCancel) {
        onRecordingCancel();
      }

      // 触觉反馈
      triggerHapticFeedback('light');
    }
  });

  const {
    startRecording,
    stopRecording,
    isRecordingInProgress,
    recordedBlob,
    recordingTime
  } = recorderControls;

  // 使用 wrapper 的状态
  const isRecording = isRecordingInProgress;
  // 🔥 综合处理状态：STT处理中或消息发送中
  const isProcessing = isSTTProcessing || isMessageSending;

  // 🔥 综合禁用状态：原有禁用 + 任何处理状态
  const isButtonDisabled = isDisabled || isProcessing;

  // 监听录音完成，处理 blob 数据 - 模仿 VoiceVisualizerTestMinimal 的方式
  useEffect(() => {
    if (recordedBlob && recordedBlob.size > 0 && recordedBlob !== lastProcessedBlobRef.current) {
      // 更新引用，避免重复处理
      lastProcessedBlobRef.current = recordedBlob;

      // 使用 ref 中的最新回调函数，避免闭包问题
      const { gestureState: currentGestureState, onRecordingComplete: currentOnComplete, onRecordingEdit: currentOnEdit, isQuickRelease } = callbacksRef.current;

      logger.info('LongPressRecordButton: Recording completed', {
        gestureState: currentGestureState,
        isQuickRelease,
        size: recordedBlob.size,
        type: recordedBlob.type,
        recordingTime: recordingTime,
        recordingDuration: recordingDuration
      });

      // 🔥 如果是快速抬手，跳过所有后续处理（包括STT和发送）
      if (isQuickRelease) {
        logger.info('LongPressRecordButton: Quick release detected, skipping all processing', {
          blobSize: recordedBlob.size,
          blobType: recordedBlob.type
        });

        // 🔥 重置快速抬手标志
        callbacksRef.current.isQuickRelease = false;

        // 🔥 确保不调用任何完成回调，直接跳到状态重置
        // 统一在这里重置状态
        logger.info('LongPressRecordButton: Resetting states after quick release');
        setIsDragging(false);
        setGestureState('normal');
        setIsDesktopHoldMode(false);
        setIsMobileHoldMode(false);
        activePointerIdRef.current = null;
        return;
      }

      // 如果是取消状态，不保存录音
      if (currentGestureState === 'dragging-cancel') {
        logger.info('LongPressRecordButton: Recording cancelled, blob discarded', {
          blobSize: recordedBlob.size,
          blobType: recordedBlob.type
        });

        // 🔥 确保不调用任何完成回调
        return;
      } else {
        // 获取当前录音时长
        let currentRecordingTime = recordingTime || recordingDuration || lastRecordingDurationRef.current || 0;

        // 如果所有来源都是 0，尝试计算经过的时间
        if (currentRecordingTime === 0 && recordingStartTimeRef.current) {
          currentRecordingTime = Date.now() - recordingStartTimeRef.current;
        }

        // 根据手势状态决定调用哪个回调
        if (currentGestureState === 'dragging-edit' && currentOnEdit) {
          logger.info('LongPressRecordButton: Calling onRecordingEdit');
          currentOnEdit(recordedBlob, currentRecordingTime);
        } else if (currentOnComplete) {
          logger.info('LongPressRecordButton: Calling onRecordingComplete');
          currentOnComplete(recordedBlob, currentRecordingTime);
        }
      }

      // 统一在这里重置状态
      logger.info('LongPressRecordButton: Resetting states after processing blob');
      setIsDragging(false);
      setGestureState('normal');
      setIsDesktopHoldMode(false); // 🔥 重置桌面保持模式
      setIsMobileHoldMode(false); // 🔥 重置移动端保持模式
      activePointerIdRef.current = null;
    }
  }, [recordedBlob, logger]);



  // 监听录音时长变化，添加时长限制和提醒
  useEffect(() => {
    if (recordingTime !== undefined) {
      setRecordingDuration(recordingTime);
      // 保存最后的录音时长
      if (recordingTime > 0) {
        lastRecordingDurationRef.current = recordingTime;
      }

      // 🔥 录音时长检查和提醒
      if (isRecording && recordingTime > 0) {

        const remainingTime = maxRecordingDuration - recordingTime / 1000;

        // 提前警告（例如剩余5秒时提醒）
        if (remainingTime <= warningThreshold && !hasShownWarning) {
          setHasShownWarning(true);
          logger.info('LongPressRecordButton: Recording time warning', {
            recordingTimeMs: recordingTime,
            recordingTimeSec: recordingTime / 1000,
            remainingTime: Math.ceil(remainingTime),
            maxDuration: maxRecordingDuration,
            warningThreshold: warningThreshold,
            calculation: `${maxRecordingDuration} - ${recordingTime / 1000} = ${remainingTime}`
          });
        }

        // 🔥 达到最大时长自动停止
        if (remainingTime <= 0) {
          logger.info('LongPressRecordButton: Max recording duration reached, auto stopping');
          addToast({
            title: '录音已达到最大时长',
            description: `已自动停止录音（${maxRecordingDuration}秒）`,
            status: 'info',
            duration: 3000,
          });

          // 自动停止录音
          callbacksRef.current.gestureState = 'normal';
          stopRecording();
          triggerHapticFeedback('medium');
        }
      }
    }
  }, [recordingTime, isRecording, maxRecordingDuration, warningThreshold, hasShownWarning, stopRecording, addToast, logger]);

  // 触觉反馈函数
  const triggerHapticFeedback = useCallback(async (type: 'light' | 'medium' | 'heavy' = 'light') => {
    if (supportsTactile) {
      try {
        if (Capacitor.isNativePlatform()) {
          const style = type === 'light' ? ImpactStyle.Light :
                       type === 'medium' ? ImpactStyle.Medium : ImpactStyle.Heavy;
          await Haptics.impact({ style });
        } else {
          // 降级到浏览器 vibrate API
          const duration = type === 'light' ? 50 : type === 'medium' ? 100 : 200;
          navigator.vibrate(duration);
        }
      } catch (error) {
        // 如果 Haptics 导入失败，降级到浏览器 API
        const duration = type === 'light' ? 50 : type === 'medium' ? 100 : 200;
        navigator.vibrate(duration);
      }
    }
  }, [supportsTactile]);

  // 处理手势状态变化
  const handleGestureStateChange = useCallback((newState: 'normal' | 'dragging-cancel' | 'dragging-edit' | 'dragging-hold') => {
    if (gestureState !== newState) {
      setGestureState(newState);
      triggerHapticFeedback('light');
    }
  }, [gestureState, triggerHapticFeedback]);

  // 🔥 删除中间处理函数，直接使用 wrapper 的方法
  // wrapper 已经处理了所有错误和竞态条件

  // 处理长按开始 - 🔥 只处理移动端触摸
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    const currentIsTouch = isTouchDevice();

    // 🔥 使用综合禁用状态
    if (isButtonDisabled) {
      return;
    }

    // 🔥 桌面端不处理 pointerDown，使用 onClick
    if (!currentIsTouch) {
      return;
    }

    // 立即阻止默认行为，包括上下文菜单
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);
    setGestureState('normal');
    activePointerIdRef.current = e.pointerId;

    // 🔥 移动端：直接开始录音
    logger.info('LongPressRecordButton: Mobile touch start recording');
    startRecording();
    triggerHapticFeedback('medium');

    // 捕获指针
    if (buttonRef.current) {
      try {
        buttonRef.current.setPointerCapture(e.pointerId);
      } catch (error) {
        // 指针捕获失败，继续执行
      }
    }
  }, [isButtonDisabled, startRecording, triggerHapticFeedback, logger]);

  // 用于保存 RecordingOverlay 的区域检测函数
  const overlayPointerHandlerRef = useRef<((e: React.PointerEvent) => void) | null>(null);

  // 处理拖拽移动 - 由于使用了 pointer capture，按钮会接收到所有移动事件
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const currentIsTouch = isTouchDevice();

    if (!currentIsTouch || !isRecording) {
      return;
    }

    // 只处理当前活跃的 pointer
    if (activePointerIdRef.current !== null && e.pointerId !== activePointerIdRef.current) {
      return;
    }

    e.preventDefault();

    // 如果 RecordingOverlay 提供了区域检测函数，使用它
    if (overlayPointerHandlerRef.current) {
      overlayPointerHandlerRef.current(e);
    }
  }, [isRecording]);

  // 处理松开
  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    // 🔥 移除 isRecording 检查，直接处理抬手事件
    // wrapper 已经处理了快速点击的竞态条件问题

    // 只处理当前活跃的 pointer
    if (activePointerIdRef.current !== null && e.pointerId !== activePointerIdRef.current) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    logger.info('LongPressRecordButton: handlePointerUp called', {
      gestureState,
      isRecording,
      isDragging,
      isMobileHoldMode,
      pointerId: e.pointerId,
      activePointerId: activePointerIdRef.current
    });

    // 立即重置 activePointerIdRef，防止快速点击时的竞态条件
    activePointerIdRef.current = null;

    // 释放指针捕获
    if (buttonRef.current && e.pointerId) {
      try {
        buttonRef.current.releasePointerCapture(e.pointerId);
      } catch (error) {
        // 指针释放失败，继续执行
      }
    }

    // 🔥 如果处于移动端保持模式，不停止录音
    if (isMobileHoldMode) {
      logger.info('LongPressRecordButton: In mobile hold mode, not stopping recording');
      return;
    }

    // 🔥 根据手势状态决定操作
    if (gestureState === 'dragging-cancel') {
      callbacksRef.current.gestureState = 'dragging-cancel';
      logger.info('LongPressRecordButton: Cancelling recording');
      // 🔥 取消时禁用STT，避免浪费钱
      enableSTTRef.current = false;

      if (onRecordingCancel) {
        onRecordingCancel();
      }
      stopRecording();
      triggerHapticFeedback('heavy');
    } else if (gestureState === 'dragging-edit') {
      callbacksRef.current.gestureState = 'dragging-edit';
      logger.info('LongPressRecordButton: Edit recording');
      stopRecording();
      triggerHapticFeedback('light');
    } else if (gestureState === 'dragging-hold') {
      // 🔥 进入保持模式，不停止录音
      logger.info('LongPressRecordButton: Entering hold mode');
      setIsMobileHoldMode(true);
      setGestureState('normal'); // 重置手势状态
      triggerHapticFeedback('medium');
      // 不调用 stopRecording()，录音继续
    } else {
      callbacksRef.current.gestureState = 'normal';
      logger.info('LongPressRecordButton: Normal stop recording');
      stopRecording();
      triggerHapticFeedback('light');
    }

    // 其他状态重置仍在 useEffect 中统一处理
  }, [gestureState, stopRecording, triggerHapticFeedback, onRecordingCancel, isDragging, isMobileHoldMode, logger]);

  // 处理指针取消
  const handlePointerCancel = useCallback((e: React.PointerEvent) => {
    // 🔥 移除 isRecording 检查，直接处理取消事件

    e.preventDefault();
    e.stopPropagation();

    // 取消录音
    callbacksRef.current.gestureState = 'dragging-cancel';
    if (onRecordingCancel) {
      onRecordingCancel();
    }

    // 🔥 直接调用 stopRecording
    stopRecording();
    triggerHapticFeedback('heavy');

    // 状态重置现在在 useEffect 中统一处理
  }, [stopRecording, onRecordingCancel, triggerHapticFeedback]);

  // 处理点击 (桌面端) - 🔥 点击直接进入保持模式
  const handleClick = useCallback((e: React.MouseEvent) => {
    const currentIsTouch = isTouchDevice();

    logger.info('LongPressRecordButton: handleClick called', {
      isTouch: currentIsTouch,
      isButtonDisabled,
      isRecording,
      isDesktopHoldMode,
      eventType: e.type,
      target: e.target,
      currentTarget: e.currentTarget
    });

    // 🔥 只处理桌面端点击，使用综合禁用状态
    if (currentIsTouch || isButtonDisabled) {
      logger.info('LongPressRecordButton: Click ignored', { isTouch: currentIsTouch, isButtonDisabled });
      return;
    }

    // 🔥 桌面端点击逻辑：如果没有录音，直接开始录音并进入保持模式
    if (!isRecording && !isDesktopHoldMode) {
      logger.info('LongPressRecordButton: Desktop click - starting recording and entering hold mode');
      startRecording();
      setIsDesktopHoldMode(true);
      triggerHapticFeedback('medium');
    } else {
      logger.info('LongPressRecordButton: Click ignored - already recording or in hold mode', {
        isRecording,
        isDesktopHoldMode
      });
    }
    // 如果已经在录音或保持模式，不做任何操作（通过覆盖层按钮操作）
  }, [isButtonDisabled, isRecording, isDesktopHoldMode, startRecording, triggerHapticFeedback, logger]);

  // 获取按钮内容（包含动画）- 🔥 参考VoiceRecordButton样式
  const getButtonContent = () => {
    const currentIsTouch = isTouchDevice();

    // 🔥 优先显示处理状态
    if (isSTTProcessing) return '正在转录...';
    if (isMessageSending) return '正在发送...';
    if (isRecording || isDesktopHoldMode || isMobileHoldMode) {
      if (gestureState === 'dragging-cancel') return '松开取消录音';
      if (gestureState === 'dragging-edit') return '松开转为文字编辑';
      if (gestureState === 'dragging-hold') return '松开进入保持模式';

      // 录音时显示波形动画和文本
      let text = '';
      if (isDesktopHoldMode || isMobileHoldMode) {
        text = '保持录音中';
      } else {
        text = currentIsTouch ? '松开发送' : '点击开始录音';
      }
      const remainingTime = maxRecordingDuration - recordingDuration / 1000;
      const showWarning = remainingTime <= warningThreshold;

      // 🔥 调试UI显示逻辑
      if (showWarning) {
        console.log('🔥 DEBUG: UI showing warning', {
          recordingDuration,
          recordingDurationInSeconds: recordingDuration / 1000,
          maxRecordingDuration,
          warningThreshold,
          remainingTime,
          showWarning
        });
      }

      return (
        <div className="flex items-center justify-center gap-2">
          <div className="flex items-center gap-1">
            <div
              className="w-1 bg-white rounded-full transition-all duration-150 ease-in-out"
              style={{
                height: '8px',
                animation: 'waveform-1 0.8s ease-in-out infinite alternate'
              }}
            ></div>
            <div
              className="w-1 bg-white rounded-full transition-all duration-150 ease-in-out"
              style={{
                height: '16px',
                animation: 'waveform-2 0.6s ease-in-out infinite alternate',
                animationDelay: '0.1s'
              }}
            ></div>
            <div
              className="w-1 bg-white rounded-full transition-all duration-150 ease-in-out"
              style={{
                height: '12px',
                animation: 'waveform-3 0.9s ease-in-out infinite alternate',
                animationDelay: '0.2s'
              }}
            ></div>
            <div
              className="w-1 bg-white rounded-full transition-all duration-150 ease-in-out"
              style={{
                height: '20px',
                animation: 'waveform-4 0.7s ease-in-out infinite alternate',
                animationDelay: '0.3s'
              }}
            ></div>
            <div
              className="w-1 bg-white rounded-full transition-all duration-150 ease-in-out"
              style={{
                height: '8px',
                animation: 'waveform-5 0.8s ease-in-out infinite alternate',
                animationDelay: '0.4s'
              }}
            ></div>
          </div>
          <span>{text}</span>
          {/* 🔥 时长警告提示 */}
          {showWarning && (
            <span className="text-yellow-200 text-sm ml-2">
              {Math.ceil(remainingTime)}s
            </span>
          )}
        </div>
      );
    }
    return currentIsTouch ? '长按说话，松开发送' : '点击开始录音';
  };

  // 获取按钮文本（用于aria-label）
  const getButtonText = () => {
    const currentIsTouch = isTouchDevice();

    // 🔥 优先显示处理状态
    if (isSTTProcessing) return '正在转录...';
    if (isMessageSending) return '正在发送...';
    if (isRecording || isDesktopHoldMode || isMobileHoldMode) {
      if (gestureState === 'dragging-cancel') return '松开取消录音';
      if (gestureState === 'dragging-edit') return '松开转为文字编辑';
      if (gestureState === 'dragging-hold') return '松开进入保持模式';

      if (isDesktopHoldMode || isMobileHoldMode) return '保持录音中';
      return currentIsTouch ? '松开发送' : '点击开始录音';
    }
    return currentIsTouch ? '长按说话，松开发送' : '点击开始录音';
  };

  // 🔥 获取按钮样式 - 参考VoiceRecordButton
  const getButtonClassName = () => {
    const baseClass = "w-full mx-1 px-4 py-2 rounded-[1.25rem] text-ios-body font-medium transition-all duration-200 ease-in-out relative overflow-hidden";

    // 🔥 处理状态时显示灰色禁用样式
    if (isProcessing) {
      return `${baseClass} bg-ios-gray4 dark:bg-ios-dark-gray4 text-ios-gray dark:text-ios-dark-gray cursor-not-allowed opacity-60`;
    }

    if (isRecording || isDesktopHoldMode || isMobileHoldMode) {
      if (gestureState === 'dragging-cancel') {
        return `${baseClass} bg-red-500 text-white`;
      }
      if (gestureState === 'dragging-edit') {
        return `${baseClass} bg-blue-500 text-white`;
      }
      if (gestureState === 'dragging-hold') {
        return `${baseClass} bg-green-400 text-white`;
      }
      // 🔥 保持模式时显示绿色
      if (isDesktopHoldMode || isMobileHoldMode) {
        return `${baseClass} bg-green-500 text-white`;
      }
      return `${baseClass} bg-red-500 text-white`;
    }

    return `${baseClass} bg-ios-gray5 dark:bg-ios-dark-gray4 text-ios-label dark:text-ios-dark-label active:bg-ios-gray4 dark:active:bg-ios-dark-gray3`;
  };

  return (
    <div className={`relative ${className}`}>
      {/* 🔥 录音按钮 - 使用原生button和VoiceRecordButton样式 */}
      <button
        ref={buttonRef}
        // 指针事件 - 移动端长按
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        // 点击事件 - 桌面端
        onClick={handleClick}
        disabled={isButtonDisabled}
        className={getButtonClassName()}
        style={{
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitTapHighlightColor: 'transparent',
          MozUserSelect: 'none',
          msUserSelect: 'none'
        }}
        onContextMenu={(e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        // 添加触摸事件处理以进一步阻止上下文菜单
        onTouchStart={(e: React.TouchEvent) => {
          if (isTouchDevice()) {
            // 阻止长按上下文菜单
            e.preventDefault();
          }
        }}
        aria-label={getButtonText()}
      >
        {children || getButtonContent()}
      </button>

      {/* 录音覆盖层 */}
      <RecordingOverlay
        isVisible={isRecording || isDesktopHoldMode || isMobileHoldMode}
        duration={recordingDuration}
        gestureState={gestureState}
        onGestureStateChange={handleGestureStateChange}
        onCancel={() => {
          logger.info('LongPressRecordButton: Cancel button clicked');
          // 🔥 取消时禁用STT，避免浪费钱
          enableSTTRef.current = false;

          // 🔥 设置取消状态，让 useEffect 知道这是取消操作
          callbacksRef.current.gestureState = 'dragging-cancel';
          setGestureState('dragging-cancel');

          if (onRecordingCancel) {
            onRecordingCancel();
          }
          setIsDesktopHoldMode(false); // 🔥 退出桌面保持模式
          setIsMobileHoldMode(false); // 🔥 退出移动端保持模式
          stopRecording(); // 这会生成 blob，但 useEffect 会根据 gestureState 丢弃它
          triggerHapticFeedback('heavy');
        }}
        onEdit={() => {
          logger.info('LongPressRecordButton: Edit button clicked');
          // 🔥 先设置手势状态和回调引用，确保useEffect能正确处理
          setGestureState('dragging-edit');
          callbacksRef.current.gestureState = 'dragging-edit';
          setIsDesktopHoldMode(false); // 🔥 退出桌面保持模式
          setIsMobileHoldMode(false); // 🔥 退出移动端保持模式
          // 🔥 延迟调用stopRecording，确保状态更新完成
          setTimeout(() => {
            stopRecording();
          }, 10);
          triggerHapticFeedback('light');
        }}
        // 传递录音控制器给覆盖层，用于波形显示
        recorderControls={recorderControls}
        // 注册区域检测函数
        onRegisterPointerHandler={(handler) => {
          overlayPointerHandlerRef.current = handler;
        }}
        // 🔥 传递时长限制信息给覆盖层
        maxRecordingDuration={maxRecordingDuration}
        warningThreshold={warningThreshold}
        // 🔥 传递嵌入模式信息给覆盖层
        isEmbedded={isEmbedded}
        // 🔥 保持模式支持（移动端和桌面端）
        onHold={isTouchDevice() ? () => {
          logger.info('LongPressRecordButton: Mobile entering hold mode');
          setIsMobileHoldMode(true);
          triggerHapticFeedback('medium');
        } : undefined}
        onSend={() => {
          logger.info('LongPressRecordButton: Send from hold mode');
          setIsMobileHoldMode(false);
          setIsDesktopHoldMode(false);
          callbacksRef.current.gestureState = 'normal';
          stopRecording();
          triggerHapticFeedback('light');
        }}
        isHoldMode={isMobileHoldMode || isDesktopHoldMode}
      />
    </div>
  );
};

export const LongPressRecordButton = memo(LongPressRecordButtonComponent);
export default LongPressRecordButton;
